import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Calendar, Clock, Award, AlertCircle } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import NotificationPopup from './NotificationPopup';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";

const StudentNotifications: React.FC = () => {
  useUiLanguage();
  const {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    newNotificationPopup,
    closeNotificationPopup
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const [chatNotifications, setChatNotifications] = useState<any[]>([]);

  // Listen for chat notifications
  React.useEffect(() => {
    const handleChatMessage = (event: CustomEvent) => {
      const chatNotification = {
        id: `chat_${Date.now()}`,
        ...event.detail,
        read: false,
        created_at: event.detail.timestamp
      };

      setChatNotifications(prev => [chatNotification, ...prev.slice(0, 4)]); // Keep only 5 most recent

      // Auto-remove after 5 seconds
      setTimeout(() => {
        setChatNotifications(prev => prev.filter(n => n.id !== chatNotification.id));
      }, 5000);
    };

    window.addEventListener('newChatMessage', handleChatMessage as EventListener);

    return () => {
      window.removeEventListener('newChatMessage', handleChatMessage as EventListener);
    };
  }, []);

  console.log('StudentNotifications render - notifications:', notifications.length, 'loading:', loading);
  const unreadCount = notifications.filter(n => !n.read).length + chatNotifications.length;
  console.log('Unread count:', unreadCount);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'training':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'promotion':
        return <Award className="h-4 w-4 text-yellow-500" />;
      case 'announcement':
        return <Bell className="h-4 w-4 text-green-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled>
        <Bell className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="relative text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-xs"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
            <span className="ml-2 text-sm font-medium">
              {unreadCount > 0 ? uiText("{{value0}} new", { value0: unreadCount }) : uiText("View all")}
            </span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-80">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{uiText("Notifications")}</h3>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs"
                >
                  {uiText("Mark all read")}</Button>
              )}
            </div>

            <ScrollArea className="max-h-80">
              {/* Chat notifications */}
              {chatNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="flex items-start space-x-3 p-3 hover:bg-accent rounded-lg cursor-pointer border-l-2 border-blue-500 mb-2"
                >
                  <div className="flex-shrink-0">
                    <Bell className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {notification.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {uiText("Just now")}</p>
                  </div>
                </div>
              ))}

              {/* Regular notifications */}
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`flex items-start space-x-3 p-3 hover:bg-accent rounded-lg cursor-pointer ${
                      !notification.read ? 'bg-blue-50 dark:bg-blue-950 border-l-2 border-blue-500' : ''
                    }`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Bell className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">{uiText("No notifications yet")}</p>
                </div>
              )}
            </ScrollArea>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Notification Popup */}
      <NotificationPopup
        notification={newNotificationPopup}
        open={!!newNotificationPopup}
        onClose={closeNotificationPopup}
        onMarkAsRead={markAsRead}
      />
    </>
  );
};

export default StudentNotifications;